import {
  notifyError,
  notifySuccess,
  useNotifications,
} from 'Logic/Notifications.js'
import { useDataValue, useDataSubmit } from 'Simple/Data.js'
import { useMutation } from 'Data/Api.js'
import mutationUpsertQuestion from './mutation-upsert-question.graphql.js'
import mutationRemoveQuestion from './mutation-remove-question.graphql.js'
import mutationRemoveManyQuestions from './mutation-remove-questions.graphql.js'
import { normalizePath, useSetFlowTo } from 'Simple/Flow.js'
import { equals } from 'Data/aggregate.js'
import produce from 'immer'
import { areDependenciesValid } from './helpers.js'

/** @type {import('Simple/types.js').useDataOnSubmit} */
export default function useDataOnSubmit(props, _) {
  let onRemoveQuestion = useOnRemoveQuestion(props)
  let onUpsertQuestion = useOnUpsertQuestion(props)

  return async function onSubmit({ value, originalValue, change, args }) {
    switch (args.type) {
      case 'remove': {
        await onRemoveQuestion(value)
        return
      }
      default: {
        await onUpsertQuestion({ value, originalValue, change })
        return
      }
    }
  }
}

function useOnRemoveQuestion(props) {
  let setFlowTo = useSetFlowTo(props.viewPath)

  let [, notify] = useNotifications()
  let [, executeMutation] = useMutation(mutationRemoveQuestion)
  let [, executeMutationMany] = useMutation(mutationRemoveManyQuestions)

  let questions = useDataValue({
    context: 'questions',
    viewPath: props.viewPath,
  })

  let submit = useDataSubmit({
    context: 'questions',
    viewPath: props.viewPath,
  })

  return async function onRemoveQuestion(value) {
    let maybeDependencies = questions
      .flatMap(question =>
        question.depends_on?.map(depends_on =>
          depends_on.id === value.id ? question.id : false
        )
      )
      .filter(Boolean)

    let mutationResponse = null

    if (maybeDependencies) {
      mutationResponse = await executeMutationMany({
        ids: [...maybeDependencies, value.id],
      })
    } else {
      mutationResponse = await executeMutation({
        id: value.id,
      })
    }

    if (mutationResponse.error) {
      notify(
        notifyError(
          'Something went wrong. Please, try again or contact support if the problem persists.'
        )
      )
    } else {
      submit({ type: 're-order', deleted: [...maybeDependencies, value.id] })
      setFlowTo(normalizePath(props.viewPath, 'No'))
    }
  }
}

function useOnUpsertQuestion(props) {
  let questions = useDataValue({
    context: 'questions',
    viewPath: props.viewPath,
  })

  let setFlowTo = useSetFlowTo(props.viewPath)

  let [, notify] = useNotifications()
  let [, executeMutation] = useMutation(mutationUpsertQuestion)

  return async function onUpsertQuestion({ value, originalValue, change }) {
    try {
      let isNewQuestion = value.id === null || value.id === undefined

      if (
        !areDependenciesValid({
          depends_on: value.depends_on,
          questions,
          question_order: value.order,
        })
      ) {
        notify(
          notifyError(
            `Dependencies have errors. All dependencies must have a question, an assigned value, and the order of the selected question must be before the current question that it is being created or edited`
          )
        )
        return
      }

      value = produce(value, next => {
        if (typeof next?.options === 'string') {
          next.options = next.options
            .split(',')
            .map(value => value?.trim())
            .filter(Boolean)
        }
        if (next.order === null) {
          next.order = questions.length + 1
        }
      })

      if (!isNewQuestion && equals(value, originalValue)) {
        setFlowTo(normalizePath(props.viewPath, 'No'))
        return
      }

      let {
        external_resource,
        created_at,
        updated_at,
        updated_by,
        created_by,
        internal,
        alert_conditions,
        alert_type,
        category,
        resource,
        validation,
        textTransform,
        canSkip,
        skipNextAction,
        hideNext,
        isInformationOnly,
        organization_id,
        hideBack,
        ...question
      } = value

      if (!question.name) {
        question.name = question.prompt
      }

      let mutationResponse = await executeMutation({
        question,
      })

      if (mutationResponse.error) {
        notify(
          notifyError(
            'Something went wrong. Please, try again or contact support if the problem persists.'
          )
        )
        return
      } else {
        notify(notifySuccess(`Question Saved.`))
      }

      if (isNewQuestion) {
        change({
          id: undefined,
          name: null,
          prompt: null,
          subprompt: null,
          type: 'text',
          options: null,
          external_resource: null,
          resource_id: null,
          resource_schema: null,
          resource_table: null,
          required: false,
          order: value.order,
          configuration_id: value.configuration_id,
          status: 'active',
        })
      }
      setFlowTo(normalizePath(props.viewPath, 'No'))
    } catch (error) {
      notify(
        notifyError(
          'Something went wrong. Please, try again or contact support if the problem persists.'
        )
      )
      throw error
    }
  }
}
